import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';

import RouteCard from 'components/logistics/fulfillmentLifecycle/components/RouteCard';
import { Badge } from 'shadcn/components/ui/Badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'shadcn/components/ui/Collapsible';
import { cn } from 'shadcn/lib/utils';

/**
 * Badge variant by section type
 */
const SECTION_BADGE_VARIANTS = {
    active: 'default',
    upcoming: 'secondary',
    unrouted: 'secondary',
    complete: 'outline',
};

/**
 * RouteStatusSection
 *
 * Collapsible section that displays a group of route cards under a titled header.
 * Used by RoutesGrid to organize routes by status (Active, Upcoming, Complete, etc.).
 *
 * @param {Object} props
 * @param {string} props.title - Section title (e.g., "Active", "Upcoming")
 * @param {string} props.variant - Section type key for badge styling
 * @param {Object[]} props.routes - Array of route objects to display
 * @param {boolean} [props.defaultOpen=false] - Whether the section starts expanded
 * @param {Function} [props.onRouteClick] - Click handler for individual route cards
 * @param {Date} [props.referenceTime] - Reference time for status message (e.g. "Pull Xm ago")
 */
function RouteStatusSection({ defaultOpen = false, onRouteClick, referenceTime, routes, title, variant }) {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    if (!routes || routes.length === 0) {
        return null;
    }

    const badgeVariant = SECTION_BADGE_VARIANTS[variant] || 'secondary';

    return (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <CollapsibleTrigger asChild>
                <button
                    type="button"
                    className="flex items-center gap-2 w-full py-2 px-1 rounded-md hover:bg-accent/50 transition-colors text-left"
                >
                    <ChevronRight
                        className={cn(
                            'h-5 w-5 text-muted-foreground transition-transform duration-200',
                            isOpen && 'rotate-90',
                        )}
                    />
                    <h3 className="text-lg font-semibold">{title}</h3>
                    <Badge variant={badgeVariant}>{routes.length}</Badge>
                </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-3 pb-2">
                    {routes.map((route) => (
                        <RouteCard
                            key={route.lineHaulId}
                            route={route}
                            onClick={onRouteClick}
                            referenceTime={referenceTime}
                        />
                    ))}
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
}

export default RouteStatusSection;
